import axios, { AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { KeycloakAuthProvider } from '@/port/provider/KeyCloakAuthProvider';
import { HTTP_TIMEOUT_MILLIS } from '@/lib/utils/env';
import type { CurrentUser } from '@/domain/entity/CurrentUser';
import type { CurrentUserProvider } from '@/domain/provider/CurrentUserProvider';
import type { PartialWithId } from '@/lib/utils/data/types';
import Logger from '@renanmalato/logger-plus';

const httpClient = axios.create({
  baseURL: '/api/',
  timeout: HTTP_TIMEOUT_MILLIS,
  headers: {
    'Accept': 'application/json'
  }
})

httpClient.interceptors.request.use(async (config: InternalAxiosRequestConfig): Promise<InternalAxiosRequestConfig> => {
  if (KeycloakAuthProvider.isAuthenticated()) {
    return KeycloakAuthProvider.updateToken().then(() => {
      config.headers.Authorization = `Bearer ${KeycloakAuthProvider.token()}`
      return config
    })
  }
  return Promise.resolve(config)
})

// ------------------------------- //
//  Cancellable Requests           //
// ------------------------------- //

export type RequestOptions = { signal?: AbortSignal }

export class RequestCancelledError extends Error {
  constructor(method: string, url: string) {
    super(`${method} ${url} cancelled`)
    this.name = 'RequestCancelledError'
  }
}

export const isRequestCancelled = (error: unknown): boolean => error instanceof RequestCancelledError

/* ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ */
/*  A Search's Requests Carry Its Id Header    */
/* ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ */

const searchIdBySignal = new WeakMap<AbortSignal, string>()

export const tagSearchSignal = (signal: AbortSignal, searchId: string) => { searchIdBySignal.set(signal, searchId) }

const requestConfig = (options?: RequestOptions) => {
  const searchId = options?.signal ? searchIdBySignal.get(options.signal) : undefined
  return { signal: options?.signal, headers: searchId ? { 'X-Search-Id': searchId } : undefined }
}

type ApiRestClient = {
  get: <T>(url:string, options?: RequestOptions) => Promise<T>
  post: <T>(url:string, data: any, options?: RequestOptions) => Promise<T>
  postFormData: <T>(url:string, formData: FormData) => Promise<T>
  put: <T>(url:string, data: T) => Promise<T>
  patch: <T extends { id: any }>(url:string, data: PartialWithId<T>) => Promise<T>
  delete: <T>(url:string) => Promise<T>
}
export const ApiClient: CurrentUserProvider &
                        ApiRestClient = {

  // Removed listing methods - listings managed via Retool iframe

  async getUser(): Promise<CurrentUser> {

    // ------------------------------------------------- //
    //                                                   //
    //                                                   //
    //    Temporary Dev Bypass for Local Storage Token - //
    //                                                   //
    //                                                   //
    // ------------------------------------------------- //

    if (import.meta.env.DEV && localStorage.getItem('token')) {
      console.warn('🟡 Mock ApiClient.getUser(): Returning dummy user');
      return {
        id: 'mock-user-id',
        givenName: 'Dev',
        familyName: 'User',
        preferredUsername: 'devuser',
        email: 'devuser@example.com',
        picture: 'img/defaults/ss-default.jpg',
        groups: [],
        roles: ['developer']
      } as unknown as CurrentUser;
    }

    // ------------------------------------------------- //
    //                                                   //
    //                                                   //
    //       Real API call with detailed logging         //
    //                                                   //
    //                                                   //
    // ------------------------------------------------- //
    

    
    try {
      
      const res = await httpClient.get('user')
      Logger.info('✅ API call successful:', 'ApiClient', { status: res.status });
      Logger.info('👤 User data received:', 'ApiClient', { name: res.data.name, id: res.data.id, roles: res.data.roles });
      return (res.data as CurrentUser)
    } catch (error: any) {
      Logger.error('💥 API call failed for /user endpoint', 'ApiClient');
      Logger.error('💥 Error status:', 'ApiClient', { status: error.response?.status });
      Logger.error('💥 Error message:', 'ApiClient', { message: error.message });
      Logger.error('💥 Error response data:', 'ApiClient', { data: error.response?.data });
      console.error('💥 Error config:', { url: error.config?.url, method: error.config?.method, headers: error.config?.headers, baseURL: error.config?.baseURL});
      throw error;
    }
  },

  // Removed unused provider methods (autocomplete, chainAutocomplete, getChainLocationsById, getPlaceGeo)


  // ------------------------------------------------- //
  //                                                   //
  //                                                   //
  //    GET Methods for CRUD operations                //
  //                                                   //
  //                                                   //
  // ------------------------------------------------- //

  async get<T>(url: string, options?: RequestOptions): Promise<T> {
    try {
      const res = await httpClient.get(url, requestConfig(options));
      return res.data as T;
    } catch (e) {
      if (axios.isCancel(e)) throw new RequestCancelledError('GET', url);
      const error = e as AxiosError;
      console.error(`GET ${url} - ${error.response?.status} - ${error.response?.statusText}`);
      throw new Error(classifyAxiosError(error, 'GET', url));
    }
  },


  // ------------------------------------------------- //
  //                                                   //
  //                                                   //
  //    POST Methods for CRUD operations               //
  //                                                   //
  //                                                   //
  // ------------------------------------------------- //


  async post<T>(url: string, body:any, options?: RequestOptions): Promise<T> {
    try {
      const res = await httpClient.post(url, body, requestConfig(options));
      return res.data as T;
    } catch (e) {
      if (axios.isCancel(e)) throw new RequestCancelledError('POST', url);
      const error = e as AxiosError;
      console.error(`POST ${url} - ${error.response?.status} - ${error.response?.statusText}`);
      console.error('Backend error details:', error.response?.data);
      throw new Error(classifyAxiosError(error, 'POST', url));
    }
  },

  // ------------------------------------------------- //
  //                                                   //
  //                                                   //
  //    POST FormData (Multipart Upload)               //
  //                                                   //
  //                                                   //
  // ------------------------------------------------- //

  async postFormData<T>(url: string, formData: FormData): Promise<T> {
    try {
      const res = await httpClient.post(url, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return res.data as T;
    } catch (e) {
      const error = e as AxiosError;

      /* ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ */
      /*  Retry Once On Network Error     */
      /* ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ */

      if (!error.response && error.code !== 'ECONNABORTED') {
        Logger.warn(`🔁 POST FormData ${url} network error — retrying once`, 'ApiClient')
        await new Promise(r => setTimeout(r, 600))
        try {
          const retryRes = await httpClient.post(url, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          return retryRes.data as T;
        } catch (e2) {
          const retryError = e2 as AxiosError;
          console.error(`POST FormData retry ${url} - ${retryError.response?.status} - ${retryError.response?.statusText}`);
          throw new Error(classifyAxiosError(retryError, 'POST', url));
        }
      }

      console.error(`POST FormData ${url} - ${error.response?.status} - ${error.response?.statusText}`);
      throw new Error(classifyAxiosError(error, 'POST', url));
    }
  },

  // ------------------------------------------------- //
  //                                                   //
  //                                                   //
  //    PUT Methods for CRUD operations                //
  //                                                   //
  //                                                   //
  // ------------------------------------------------- //

  async put<T>(url: string, data: T): Promise<T> {
    try {
      const res = await httpClient.put(url, data);
      return res.data as T;
    } catch (e) {
      const error = e as AxiosError;
      console.error(`PUT ${url} - ${error.response?.status} - ${error.response?.statusText}`);
      console.error('Backend error details:', error.response?.data);
      throw new Error(classifyAxiosError(error, 'PUT', url));
    }
  },

  // ------------------------------------------------- //
  //                                                   //
  //                                                   //
  //    PATCH Methods for CRUD operations              //
  //                                                   //
  //                                                   //
  // ------------------------------------------------- //

  async patch<T extends { id: any }>(url: string, data: PartialWithId<T>): Promise<T> {
    try {
      const res = await httpClient.patch(url, data, {
        headers: {
          'Content-Type': 'application/merge-patch+json'
        }
      });
      return res.data as T;
    } catch (e) {
      const error = e as AxiosError;
      console.error(`PATCH ${url} - ${error.response?.status} - ${error.response?.statusText}`);
      console.error('Backend error details:', error.response?.data);
      throw new Error(classifyAxiosError(error, 'PATCH', url));
    }
  },

  // ------------------------------------------------- //
  //                                                   //
  //                                                   //
  //    DELETE Methods for CRUD operations             //
  //                                                   //
  //                                                   //
  // ------------------------------------------------- //

  async delete<T>(url: string): Promise<T> {
    try {
      const res = await httpClient.delete(url);
      return res.data as T;
    } catch (e) {
      const error = e as AxiosError;
      console.error(`DELETE ${url} - ${error.response?.status} - ${error.response?.statusText}`);
      console.error('Backend error details:', error.response?.data);
      throw new Error(classifyAxiosError(error, 'DELETE', url));
    }
  },



}

// ------------------------------------------------- //
//                                                   //
//                                                   //
//    Classify Axios Error                           //
//    Detects timeout / network before backend msg   //
//                                                   //
// ------------------------------------------------- //
const classifyAxiosError = (error: AxiosError, method: string, url: string): string => {
  const timeoutSeconds = Math.round(HTTP_TIMEOUT_MILLIS / 1000)

  // Axios timeout — request was aborted client-side (no response received)
  if (error.code === 'ECONNABORTED') {
    Logger.warn(`⏱️ ${method} ${url} timed out after ${timeoutSeconds}s`, 'ApiClient')
    return `Request timed out after ${timeoutSeconds}s - the server is still processing. Try a more specific search or smaller dataset.`
  }

  // Network error — no response at all (server unreachable, CORS, connection reset)
  if (!error.response) {
    Logger.warn(`🌐 ${method} ${url} network error: ${error.message}`, 'ApiClient')
    return `Network error on ${url}: ${error.message}`
  }

  // HTTP error — server responded with an error status
  return extractMeaningfulError(error.response?.data, url)
}


// ------------------------------------------------- //
//                                                   //
//                                                   //
//    Extract Meaningful Error from Backend          //
//                                                   //
//                                                   //
// ------------------------------------------------- //
const extractMeaningfulError = (errorData: any, url: string): string => {
  if (typeof errorData === 'string') return errorData;

  // Try different error fields
  const potentialError = errorData?.message || errorData?.error || errorData?.details;
  if (!potentialError) return `Failed to fetch data from ${url}`;

  // If it's a complex Java error with stack trace, try to extract the meaningful part
  if (typeof potentialError === 'string' && potentialError.includes('java.lang.RuntimeException')) {
    // Look for patterns like "Original error: X" or "Crexi Retool error: X"
    const originalErrorMatch = potentialError.match(/Original error: ([^,]+)/);
    if (originalErrorMatch) return originalErrorMatch[1];

    const crexiErrorMatch = potentialError.match(/Crexi Retool error: ([^,]+)/);
    if (crexiErrorMatch) return crexiErrorMatch[1];

    // Look for the main RuntimeException message
    const runtimeErrorMatch = potentialError.match(/java\.lang\.RuntimeException: ([^\\n\\r]+)/);
    if (runtimeErrorMatch) {
      const message = runtimeErrorMatch[1];
      // If it's a nested error message, try to extract the simple part
      if (message.includes('Original error:')) {
        const simpleMatch = message.match(/Original error: ([^,]+)/);
        return simpleMatch ? simpleMatch[1] : message;
      }
      return message;
    }
  }

  return potentialError;
};
